export const UNIFIED_CORRECTION_KEY = "unified";

const LAYER_COMMENT_RE = /^layer:(\d+)$/i;
const LAYER_CLUSTER_MS = 30 * 60 * 1000;

export const correctionCellKey = (sectionId, blockKey) =>
  `${sectionId}:${blockKey}`;

export const ESTIMATE_CORRECTION_MODULES = ["bim", "qc", "pd", "pm"];

export const MODULE_CORRECTION_LABELS = {
  bim: "BIM",
  qc: "Контроль качества",
  pd: "Ведение проекта",
  pm: "Управление проектом",
};

export const isModuleCorrectionKey = (key) =>
  String(key || "").startsWith("m:");

export const normalizeCorrectionRole = (role) =>
  String(role || "")
    .trim()
    .toLowerCase();

export const moduleCorrectionCellKey = (module, role, sectionId) =>
  `m:${module}:${normalizeCorrectionRole(role)}:${
    sectionId == null || sectionId === "" ? "" : String(sectionId)
  }`;

export const correctionLayerComment = (number) => `layer:${Number(number) || 1}`;

export const parseCorrectionLayerNumber = (comment) => {
  const match = String(comment || "")
    .trim()
    .match(LAYER_COMMENT_RE);
  return match ? parseInt(match[1], 10) : null;
};

export const readCorrectionValue = (correction, sectionId, blockKey) => {
  const raw = correction?.values?.[correctionCellKey(sectionId, blockKey)];
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

export const formatCorrectionDelta = (value) => {
  const n = Number(value) || 0;
  if (n > 0) return `+${n}`;
  return String(n);
};

export const getBlockCorrectionKey = (block, index) => {
  const teiId = block?.block?.tei_id;
  if (teiId != null) return String(teiId);
  const blockId = block?.block?.id;
  if (blockId != null) return `id_${blockId}`;
  return `idx_${index}`;
};

export const parseCorrectionTeiId = (blockKey) => {
  if (blockKey == null || blockKey === UNIFIED_CORRECTION_KEY) return null;
  const asNumber = Number(blockKey);
  if (Number.isFinite(asNumber) && String(blockKey).trim() !== "") {
    return asNumber;
  }
  return null;
};

export const getEstimateSectionIds = (estimateData) => {
  const ids = [
    ...(estimateData?.construction_design || []),
    ...(estimateData?.construction_design_project || []),
  ]
    .map((item) => item?.section?.id)
    .filter((id) => id != null);
  return [...new Set(ids)];
};

export const emptyCorrectionImpact = () => ({
  day_efforts: 0,
  bonus_day_efforts: 0,
  money_expenditures: 0,
  bonus_money_expenditures: 0,
});

export const correctionImpactFromRecord = (record, fallback) => ({
  day_efforts:
    record?.day_efforts != null
      ? Number(record.day_efforts) || 0
      : Number(fallback?.day_efforts) || 0,
  bonus_day_efforts:
    record?.bonus_day_efforts != null
      ? Number(record.bonus_day_efforts) || 0
      : Number(fallback?.bonus_day_efforts) || 0,
  money_expenditures:
    record?.money_expenditures != null
      ? Number(record.money_expenditures) || 0
      : Number(fallback?.money_expenditures) || 0,
  bonus_money_expenditures:
    record?.bonus_money_expenditures != null
      ? Number(record.bonus_money_expenditures) || 0
      : Number(fallback?.bonus_money_expenditures) || 0,
});

export const addCorrectionImpacts = (left, right) => ({
  day_efforts: (Number(left?.day_efforts) || 0) + (Number(right?.day_efforts) || 0),
  bonus_day_efforts:
    (Number(left?.bonus_day_efforts) || 0) +
    (Number(right?.bonus_day_efforts) || 0),
  money_expenditures:
    (Number(left?.money_expenditures) || 0) +
    (Number(right?.money_expenditures) || 0),
  bonus_money_expenditures:
    (Number(left?.bonus_money_expenditures) || 0) +
    (Number(right?.bonus_money_expenditures) || 0),
});

export const correctionImpactMoney = (impact) =>
  (Number(impact?.money_expenditures) || 0) +
  (Number(impact?.bonus_money_expenditures) || 0);

export const isNonZeroCorrectionImpact = (impact) =>
  (Number(impact?.day_efforts) || 0) !== 0 ||
  (Number(impact?.bonus_day_efforts) || 0) !== 0 ||
  (Number(impact?.money_expenditures) || 0) !== 0 ||
  (Number(impact?.bonus_money_expenditures) || 0) !== 0;

export const formatSignedDelta = (value, { money = false } = {}) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "";
  const sign = n > 0 ? "+" : "−";
  const abs = Math.abs(n);
  const body = money
    ? Math.round(abs).toLocaleString("ru-RU")
    : String(Number(abs.toFixed(2)));
  return `${sign}${body}`;
};

export const readCorrectionImpact = (correction, sectionId, blockKey) => {
  const key = correctionCellKey(sectionId, blockKey);
  const stored = correction?.impacts?.[key];
  if (stored) return correctionImpactFromRecord(stored);
  return {
    ...emptyCorrectionImpact(),
    day_efforts: readCorrectionValue(correction, sectionId, blockKey),
  };
};

export const sumCorrectionImpact = (corrections, sectionId, blockKey) =>
  (corrections || []).reduce(
    (acc, layer) =>
      addCorrectionImpacts(
        acc,
        readCorrectionImpact(layer, sectionId, blockKey),
      ),
    emptyCorrectionImpact(),
  );

const uniqueLayerKeys = (layer) => [
  ...new Set([
    ...Object.keys(layer?.values || {}),
    ...Object.keys(layer?.impacts || {}),
  ]),
];

export const sumSectionCorrectionImpact = (corrections, sectionId) => {
  const prefix = `${sectionId}:`;
  return (corrections || []).reduce((acc, layer) => {
    uniqueLayerKeys(layer).forEach((key) => {
      if (isModuleCorrectionKey(key) || !key.startsWith(prefix)) return;
      acc = addCorrectionImpacts(
        acc,
        readCorrectionImpact(layer, sectionId, key.slice(prefix.length)),
      );
    });
    return acc;
  }, emptyCorrectionImpact());
};

export const readImpactByCellKey = (correction, key) => {
  const stored = correction?.impacts?.[key];
  if (stored) return correctionImpactFromRecord(stored);
  const n = Number(correction?.values?.[key]);
  return {
    ...emptyCorrectionImpact(),
    day_efforts: Number.isFinite(n) ? n : 0,
  };
};

export const readModuleCorrectionValue = (
  correction,
  module,
  role,
  sectionId,
) => {
  const raw =
    correction?.values?.[moduleCorrectionCellKey(module, role, sectionId)];
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

export const readModuleCorrectionImpact = (
  correction,
  module,
  role,
  sectionId,
) => {
  const key = moduleCorrectionCellKey(module, role, sectionId);
  const stored = correction?.impacts?.[key];
  if (stored) return correctionImpactFromRecord(stored);
  return {
    ...emptyCorrectionImpact(),
    day_efforts: readModuleCorrectionValue(
      correction,
      module,
      role,
      sectionId,
    ),
  };
};

export const sumModuleRoleCorrectionImpact = (
  corrections,
  module,
  role,
  sectionId,
) =>
  (corrections || []).reduce(
    (acc, layer) =>
      addCorrectionImpacts(
        acc,
        readModuleCorrectionImpact(layer, module, role, sectionId),
      ),
    emptyCorrectionImpact(),
  );

export const sumModuleCorrectionImpact = (corrections, module, roles) =>
  (roles || []).reduce(
    (acc, role) =>
      addCorrectionImpacts(
        acc,
        sumModuleRoleCorrectionImpact(
          corrections,
          module,
          role?.role,
          role?.section_id,
        ),
      ),
    emptyCorrectionImpact(),
  );

export const getModuleCorrectionFooter = (
  corrections,
  module,
  roles,
  base = {},
) => {
  const extra = sumModuleCorrectionImpact(corrections, module, roles);
  const extraMoney = correctionImpactMoney(extra);
  return {
    extra,
    extraMoney,
    changed: isNonZeroCorrectionImpact(extra),
    totalDirect: (Number(base.direct) || 0) + extra.money_expenditures,
    totalBonus: (Number(base.bonus) || 0) + extra.bonus_money_expenditures,
    total: (Number(base.total) || 0) + extraMoney,
  };
};

export const sumAllCorrectionImpact = (corrections) =>
  (corrections || []).reduce((acc, layer) => {
    uniqueLayerKeys(layer).forEach((key) => {
      acc = addCorrectionImpacts(acc, readImpactByCellKey(layer, key));
    });
    return acc;
  }, emptyCorrectionImpact());

export const splitClientAmountByNds = (amount, withNds, ndsPercent) => {
  const raw = Number(amount) || 0;
  const rate = Number(ndsPercent) || 0;
  if (!raw) {
    return { income: 0, contract: 0, nds: 0 };
  }
  if (!rate) {
    return { income: raw, contract: raw, nds: 0 };
  }
  if (withNds) {
    const income = raw / (1 + rate / 100);
    return { income, contract: raw, nds: raw - income };
  }
  const contract = raw * (1 + rate / 100);
  return { income: raw, contract, nds: contract - raw };
};

export const emptyIncomeCorrectionTotals = () => ({
  income: 0,
  contract: 0,
  nds: 0,
});

export const normalizeIncomeCorrection = (record) => {
  if (!record || typeof record !== "object") return null;
  if (Array.isArray(record.items) || record.total) return null;
  const amount = Number(record.amount);
  const hasAmount = Number.isFinite(amount) && amount !== 0;
  if (record.id == null && !hasAmount) return null;
  return {
    id: record.id ?? null,
    project_id: record.project_id ?? null,
    version: record.version ?? null,
    amount: Number.isFinite(amount) ? amount : 0,
    nds_included_in_amount: Boolean(record.nds_included_in_amount),
    nds_percent: Number(record.nds_percent) || 0,
    amount_before_nds: Number(record.amount_before_nds) || 0,
    amount_with_nds: Number(record.amount_with_nds) || 0,
    nds_amount: Number(record.nds_amount) || 0,
    comment: record.comment || "",
    created_by_user_id: record.created_by_user_id ?? null,
    created_at: record.created_at || null,
  };
};

export const parseIncomeCorrectionPayload = (payload) => {
  if (!payload) return { items: [], total: null };
  if (Array.isArray(payload)) {
    return {
      items: payload.map(normalizeIncomeCorrection).filter(Boolean),
      total: null,
    };
  }
  if (
    typeof payload === "object" &&
    (Array.isArray(payload.items) || payload.total)
  ) {
    const rawTotal = payload.total;
    return {
      items: (payload.items || []).map(normalizeIncomeCorrection).filter(Boolean),
      total: rawTotal
        ? {
            income: Number(rawTotal.amount_before_nds) || 0,
            contract: Number(rawTotal.amount_with_nds) || 0,
            nds: Number(rawTotal.nds_amount) || 0,
          }
        : null,
    };
  }
  const one = normalizeIncomeCorrection(payload);
  return { items: one ? [one] : [], total: null };
};

export const incomeCorrectionListTotals = (items, ndsPercent, serverTotal) => {
  const serverIncome = Number(serverTotal?.income);
  const serverContract = Number(serverTotal?.contract);
  const serverNds = Number(serverTotal?.nds);
  if (
    serverTotal &&
    ((Number.isFinite(serverIncome) && serverIncome !== 0) ||
      (Number.isFinite(serverContract) && serverContract !== 0) ||
      (Number.isFinite(serverNds) && serverNds !== 0))
  ) {
    return {
      income: serverIncome || 0,
      contract: serverContract || 0,
      nds: serverNds || 0,
    };
  }
  return (items || []).reduce((acc, item) => {
    const part = incomeCorrectionTotals(
      item,
      item?.nds_percent || ndsPercent,
    );
    return {
      income: acc.income + part.income,
      contract: acc.contract + part.contract,
      nds: acc.nds + part.nds,
    };
  }, emptyIncomeCorrectionTotals());
};

export const incomeCorrectionTotals = (record, ndsPercent) => {
  if (!record) return emptyIncomeCorrectionTotals();
  const hasServerSplits =
    record.amount_before_nds != null || record.amount_with_nds != null;
  if (hasServerSplits) {
    const income = Number(record.amount_before_nds);
    const contract = Number(record.amount_with_nds);
    if (
      (Number.isFinite(income) && income !== 0) ||
      (Number.isFinite(contract) && contract !== 0)
    ) {
      return {
        income: income || 0,
        contract: contract || 0,
        nds: Number(record.nds_amount) || 0,
      };
    }
  }
  return splitClientAmountByNds(
    record.amount,
    record.nds_included_in_amount,
    ndsPercent,
  );
};

export const formatIncomeCorrectionAmount = (record) => {
  const amount = Number(record?.amount) || 0;
  if (!amount) return "—";
  const percent = Number(record?.nds_percent) || 0;
  const vatLabel = record?.nds_included_in_amount ? "с НДС" : "без НДС";
  const percentLabel = percent > 0 ? ` ${percent}%` : "";
  return `${Math.round(amount).toLocaleString("ru-RU")} ₸ (${vatLabel}${percentLabel})`;
};

export const pairIncomeCorrectionsToLayers = (items, layers) => {
  const byLayer = new Map();
  const unmatched = [];
  (items || []).forEach((item) => {
    const layerNumber = parseCorrectionLayerNumber(item?.comment);
    if (layerNumber != null) {
      byLayer.set(layerNumber, item);
      return;
    }
    unmatched.push(item);
  });
  unmatched.sort((a, b) => {
    const timeA = new Date(a?.created_at || 0).getTime();
    const timeB = new Date(b?.created_at || 0).getTime();
    return timeA - timeB;
  });
  const used = new Set(byLayer.keys());
  (layers || [])
    .map((layer) => Number(layer?.number))
    .filter((number) => Number.isFinite(number))
    .sort((a, b) => a - b)
    .forEach((number) => {
      if (used.has(number) || !unmatched.length) return;
      byLayer.set(number, unmatched.shift());
      used.add(number);
    });
  return byLayer;
};

export const findIncomeCorrectionForLayer = (items, layers, layerNumber) => {
  const number = Number(layerNumber);
  if (!Number.isFinite(number)) return null;
  return pairIncomeCorrectionsToLayers(items, layers).get(number) || null;
};

export const ensureLayersForIncomeCorrections = (layers, incomeItems) => {
  const next = [...(layers || [])];
  const existing = new Set(next.map((layer) => Number(layer?.number)));
  (incomeItems || []).forEach((item) => {
    const number = parseCorrectionLayerNumber(item?.comment);
    if (number == null || existing.has(number)) return;
    next.push(emptyLayer(number, item?.created_at));
    existing.add(number);
  });
  return next.sort((a, b) => a.number - b.number);
};

export const preserveDraftCorrectionLayer = (nextLayers, prevLayers) => {
  const draft = (prevLayers || []).find((layer) => layer.isDraft);
  if (!draft) return nextLayers || [];
  const alreadyHas = (nextLayers || []).some(
    (layer) => layer.number === draft.number,
  );
  if (alreadyHas) return nextLayers || [];
  return [...(nextLayers || []), draft];
};

export const createCorrectionLayer = (existing = []) => {
  const numbers = existing.map((item) => Number(item?.number) || 0);
  const nextNumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
  return {
    id: `draft-${Date.now()}_${nextNumber}`,
    number: nextNumber,
    createdAt: new Date().toISOString(),
    values: {},
    recordIds: {},
    impacts: {},
    isDraft: true,
  };
};

const emptyLayer = (number, createdAt) => ({
  id: `layer-${number}`,
  number,
  createdAt: createdAt || null,
  values: {},
  recordIds: {},
  impacts: {},
  isDraft: false,
});

const withRecordTimestamp = (layer, record) => {
  const createdAt = record.created_at || layer.createdAt;
  return {
    ...layer,
    createdAt:
      layer.createdAt && createdAt
        ? new Date(createdAt) < new Date(layer.createdAt)
          ? createdAt
          : layer.createdAt
        : createdAt || layer.createdAt,
  };
};

const addWorkRecordToLayer = (layer, record) => {
  const sectionId = record?.section_id;
  if (sectionId == null || record?.id == null) return layer;
  const blockKey =
    record?.tei_id == null ? UNIFIED_CORRECTION_KEY : record.tei_id;
  const key = correctionCellKey(sectionId, blockKey);
  const next = withRecordTimestamp(layer, record);
  return {
    ...next,
    values: {
      ...next.values,
      [key]: Number(record.day_efforts) || 0,
    },
    recordIds: {
      ...next.recordIds,
      [key]: record.id,
    },
    impacts: {
      ...(next.impacts || {}),
      [key]: correctionImpactFromRecord(record, next.impacts?.[key]),
    },
  };
};

const addModuleRecordToLayer = (layer, record) => {
  const module = record?.module;
  const role = record?.role;
  if (!module || !role || record?.id == null) return layer;
  const key = moduleCorrectionCellKey(module, role, record.section_id);
  const next = withRecordTimestamp(layer, record);
  return {
    ...next,
    values: {
      ...next.values,
      [key]: Number(record.day_efforts) || 0,
    },
    recordIds: {
      ...next.recordIds,
      [key]: record.id,
    },
    impacts: {
      ...(next.impacts || {}),
      [key]: correctionImpactFromRecord(record, next.impacts?.[key]),
    },
  };
};

const addRecordToLayer = (layer, record) => {
  if (record?.module) return addModuleRecordToLayer(layer, record);
  return addWorkRecordToLayer(layer, record);
};

const assignLayerNumbersByTime = (records) => {
  const sorted = [...records].sort((a, b) => {
    const timeA = new Date(a?.created_at || 0).getTime();
    const timeB = new Date(b?.created_at || 0).getTime();
    return timeA - timeB;
  });
  let layerNumber = 1;
  let layerStart = null;
  return sorted.map((record) => {
    const time = new Date(record?.created_at || 0).getTime();
    if (layerStart == null) layerStart = time;
    if (Number.isFinite(time) && time - layerStart > LAYER_CLUSTER_MS) {
      layerNumber += 1;
      layerStart = time;
    }
    return { record, layerNumber };
  });
};

export const buildCorrectionLayersFromRecords = (records) => {
  const items = (records || []).filter((item) => item?.id != null);
  if (!items.length) return [];

  const hasLayerComments = items.some(
    (item) => parseCorrectionLayerNumber(item.comment) != null,
  );

  const assigned = hasLayerComments
    ? items.map((record) => ({
        record,
        layerNumber: parseCorrectionLayerNumber(record.comment) || 1,
      }))
    : assignLayerNumbersByTime(items);

  const layersByNumber = new Map();
  assigned.forEach(({ record, layerNumber }) => {
    const number = Number(layerNumber) || 1;
    const current = layersByNumber.get(number) || emptyLayer(number, record.created_at);
    layersByNumber.set(number, addRecordToLayer(current, record));
  });

  return [...layersByNumber.values()].sort((a, b) => a.number - b.number);
};

export const buildCorrectionLayersFromDetailed = (sectionDetails) => {
  const layer = emptyLayer(1, null);
  let hasAny = false;

  Object.values(sectionDetails || {}).forEach((detail) => {
    const sectionId = detail?.section_id;
    (detail?.block_expenditures || []).forEach((block) => {
      const teiId = block?.block?.tei_id;
      const correctionId = block?.correction_id;
      const efforts = Number(block?.correction_day_efforts);
      if (sectionId == null || teiId == null) return;
      if (correctionId == null && !Number.isFinite(efforts)) return;
      if (correctionId == null && !efforts) return;
      hasAny = true;
      const key = correctionCellKey(sectionId, teiId);
      layer.values[key] = Number.isFinite(efforts) ? efforts : 0;
      if (correctionId != null) layer.recordIds[key] = correctionId;
      layer.impacts[key] = {
        day_efforts: Number.isFinite(efforts) ? efforts : 0,
        bonus_day_efforts: Number(block?.correction_bonus_day_efforts) || 0,
        money_expenditures: Number(block?.correction_money_expenditures) || 0,
        bonus_money_expenditures:
          Number(block?.correction_bonus_money_expenditures) || 0,
      };
    });
  });

  return hasAny ? [layer] : [];
};

export const collectWorkCorrectionRecordIds = (layers = []) =>
  layers.flatMap((layer) =>
    Object.entries(layer?.recordIds || {})
      .filter(([key]) => !isModuleCorrectionKey(key))
      .map(([, id]) => id)
      .filter((id) => id != null),
  );

export const collectModuleCorrectionRecordIds = (layers = []) =>
  layers.flatMap((layer) =>
    Object.entries(layer?.recordIds || {})
      .filter(([key]) => isModuleCorrectionKey(key))
      .map(([, id]) => id)
      .filter((id) => id != null),
  );

export const collectLayerRecordIds = (layers = []) => [
  ...collectWorkCorrectionRecordIds(layers),
  ...collectModuleCorrectionRecordIds(layers),
];

export const countFilledCorrectionCells = (layer) =>
  Object.values(layer?.values || {}).filter((value) => Number(value) !== 0)
    .length;

export const isEmptyCorrectionLayer = (layer) => {
  if (!layer) return true;
  const hasRecords = Object.keys(layer.recordIds || {}).length > 0;
  const hasValues = Object.values(layer.values || {}).some(
    (value) => Number(value) !== 0,
  );
  return !hasRecords && !hasValues;
};
